<script setup lang="ts">
import { ref, computed } from 'vue'
import { useHead } from '#imports'
import { useAsyncData } from '#app'
import { useScreen } from '@/composables/useScreen'
import { obscureEmail, obscureString, getProfessionStatus } from '@/helpers'

const config = useRuntimeConfig()
const route = useRoute()
const { t, locale } = useI18n()
const axios = useAxios()
const { isMobileView } = useScreen()
const { user } = useSession()

const reviewModalVisible = ref(false)
const phoneVisible = ref(false)
const emailVisible = ref(false)
const showObscured = computed(() => !!route.query.show && !!user.value)
const loginModalVisible = ref(false)

// Fetch everything in one SSR-friendly call
const { data: mainData, status, error: mainError } = useAsyncData(`lawyer-full-data-${route.params.slug}`, async () => {
  // Fetch main lawyer by slug
  const { data: lawyerData } = await axios.get(`lawyers/by-slug/${route.params.slug}`)

  // Once we have lawyerData, fetch reviews & competing in parallel
  const [reviewsRes, competingRes] = await Promise.all([
    axios.get(`lawyer-reviews/${lawyerData.id}`),
    axios.get('/competing-lawyers', { params: { lawyer_slug: lawyerData.slug, limit: (lawyerData.photo? 2 : 4) } })
  ])

  // Return a single combined object
  return {
    lawyer: lawyerData,
    reviews: reviewsRes?.data ?? [],
    competingLawyersList: competingRes?.data ?? []
  }
})

const isLoading = computed(() => status.value === 'pending')
const lawyer = computed(() => mainData.value?.lawyer || {})
const reviews = computed(() => mainData.value?.reviews || [])
const competingLawyersList = computed(() => mainData.value?.competingLawyersList || [])

const parsedLanguages = computed(() => {
  const languagesObj = lawyer.value.languages || {}
  const languagesArray = Object.values(languagesObj)
  return languagesArray.map(language => `${language} ${t('language_abbreviation')}.`).join(', ')
})

function getLawyerExperience (qualificationYears: number) {
  const experience = new Date().getFullYear() - qualificationYears
  return experience + t('year_abbreviation')
}

function scrollToReviews () {
  const element = document.getElementById('reviews')
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

function showPhone () {
  sendGAEvent('lawyer_show_contacts_clicked')

  if (!user.value)
    loginModalVisible.value = true
  else
    phoneVisible.value = true
}

function showEmail () {
  sendGAEvent('lawyer_show_contacts_clicked')

  if (!user.value) 
    loginModalVisible.value = true
  else
    emailVisible.value = true
}

function sendGAEvent (eventName: string) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, {
      event_category: 'button',
      lawyer_slug: route.params.slug,
      firm_lawyer_id: lawyer.value.firm_lawyer_id
    })
  }
}

function navigateToLogin () {
  const redirectPath = btoa(`${route.fullPath}?show=true`)
  return navigateTo(`/${t('login_path')}?redirect=${redirectPath}`)
}

const reviewsLocalizedEnding = computed(() => {
  if (!reviews.value) return ''

  if (locale.value === 'lt') {
    if (reviews.value.length === 0) {
      return 'ų'
    } else if (reviews.value.length === 1) {
      return 'as'
    } else {
      return 'ai'
    }
  } else {
    // English
    return reviews.value.length === 1 ? '' : 's'
  }
})

// ---- SEO improvements ----
// Dynamic <title> and <meta> description
const pageTitle = computed(() => {
  return lawyer.value?.name
    ? `${lawyer.value.name} ${lawyer.value.lastname}`
    : 'Loading...'
})

const canonicalLink = computed(() => {
  const baseUrl = config.public.baseURL.includes('localhost')
    ? 'https://geriteisininkai.lt'
    : config.public.baseURL
  return `${baseUrl}${route.fullPath}`
})

// JSON-LD Structured Data
const structuredData = computed(() => {
  // If data not loaded yet, return empty
  if (!lawyer.value.id) return {}

  // Example schema for a Person (Lawyer)
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": `${lawyer.value.name} ${lawyer.value.lastname}`,
    "jobTitle": "Lawyer",
    "image": lawyer.value?.photo || '',
    "url": canonicalLink.value,
    "worksFor": {
      "@type": "Organization",
      "name": lawyer.value?.firm?.name || ''
    },
    "email": lawyer.value?.email || '',
    "telephone": lawyer.value?.phone || '',
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": lawyer.value?.active_reviews_rating || 0,
      "ratingCount": reviews.value?.length || 0
    }
  }
})

useHead({
  title: pageTitle,
  meta: [
    {
      name: 'description',
      content: computed(() => {
        // Create dynamic meta desc from lawyer bio if available
        const lawyerBio = mainData.value?.lawyer?.bio || ''
        return lawyerBio.slice(0, 155)
      }),
    },
    // Open Graph
    {
      property: 'og:title',
      content: pageTitle.value,
    },
    {
      property: 'og:description',
      content: computed(() => mainData.value?.lawyer?.bio || ''),
    },
    {
      property: 'og:image',
      content: computed(() => mainData.value?.lawyer?.photo || '@/assets/images/lawyer.png'),
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: canonicalLink.value,
    },
  ],
  // Inject JSON-LD into <head>
  script: [
    {
      type: 'application/ld+json',
      // 'children' is where the actual JSON string goes
      children: computed(() => JSON.stringify(structuredData.value))
    }
  ]
})
</script>
<template>
  <div v-if="!isLoading" class="font-redhat font-medium">
    <div class="container mt-8 text-sm lg:text-md xl:flex">
      <div class="md:flex md:justify-center">
        <div class="h-full w-full aspect-[87.4/100] xs:h-[484px] xs:w-[423px] rounded-[40px] relative bg-white-500 select-none">
          <GTImage :src="lawyer.photo" alt="lawyer photo" class="rounded-[20px] md:rounded-[40px] h-full w-full object-cover object-center">
            <template #fallback>
              <img src="/images/lawyer.svg" alt="default lawyer icon" class="translate-y-12 xl:translate-y-18 md:max-w-[333px] md:max-h-[333px] mx-auto object-cover object-center">
            </template>
          </GTImage>
          <div v-if="lawyer.photo">
            <div class="flex items-center justify-center">
              <div class="rounded-[28px] w-[95%] h-[48px] absolute bottom-3 z-10 text-white bg-lightBlue flex items-center justify-center">
                <rating-stars :value="lawyer.active_reviews_rating" :text-size="isMobileView? 16 : 18" class="*:mx-0.5"/>
                <button v-if="lawyer.photo" class="ml-3 text-sm mt-0.5 underline" @click="scrollToReviews">
                  {{ reviews.length }} {{ $t('reviews', {ending:reviewsLocalizedEnding}) }}
                </button>
              </div>
            </div>
          </div>
          <div class="bg-white-300 top-[88px] rounded-r-[12px] w-[88px] h-[52px] absolute z-10 text-darkBlue flex items-center justify-center p-3 text-sm">
            {{ $t(getProfessionStatus(lawyer.lawyer_profession_status)) }}
          </div>
        </div>
      </div>

      <div class="xl:ml-12 text-darkBlue w-full mr-auto">
        <div class="mt-8 lg:mt-[43px] sm:flex items-center justify-center">
          <h1 class="text-30 lg:text-60 leading-tight text-darkBlue lg:mr-2">
            {{ `${lawyer.name ?? $t('name')} ${lawyer.lastname ?? $t('lastname')}` }}
          </h1>
          <div class="w-full sm:w-[190px] ml-0 sm:ml-auto mt-4 sm:mt-0 font-branding">
            <div class="h-10 bg-lightBlue text-white flex items-center justify-center" :class="lawyer.hourly_consultation_price? 'rounded-t-[12px]' : 'rounded-[12px]'">
              <div class="text-[22px] lg:text-[28px]">
                {{ lawyer.hourly_consultation_price ?? '- ' }}€
              </div>
              <div class="text-xs flex lg:block leading-none">
                <div>/{{ $t('hour_abbreviation') }}</div>
                <div v-if="lawyer.is_vat_payer" class="ml-1">({{ $t('including_vat') }})</div>
              </div>
            </div>
            <div v-if="lawyer.hourly_consultation_price" class="min-h-[25px] border border-lightBlue rounded-b-[12px] text-xs text-lightBlue p-1.5 leading-[13px] px-2 flex flex-wrap h-full gap-x-1 justify-center">
              {{ $t('introductory_meeting') }}
              <div class="font-semibold">29€</div>
              <div v-if="lawyer.is_vat_payer">({{ $t('including_vat') }})</div>
              {{ $t('or') }}
              <div class="font-semibold">{{ $t('free') }}</div>
            </div>
          </div>
        </div>

        <div v-if="lawyer.qualification_years > 0" class="mt-4 flex items-center">
          <IconLawyerAndFirmBriefcase class="text-lightBlue size-5 lg:size-6" />
          <div class="ml-3">
            {{ getLawyerExperience(lawyer.qualification_years) }} {{ $t('law_practice') }}
          </div>
        </div>

        <div v-if="lawyer.firm" class="mt-4 flex items-center">
          <IconLawyerAndFirmBuilding class="text-lightBlue size-5 lg:size-6" />
          <NuxtLinkLocale
            :to="{ name: 'firm-slug', params: { slug: lawyer.firm.slug } }"
            class="ml-3 transition hover:text-lightBlue hover:underline"
          >
            {{ lawyer.firm.name }}
          </NuxtLinkLocale>
        </div>

        <div v-if="lawyer.position_in_office" class="mt-4 flex items-center">
          <IconPositionInOffice class="text-lightBlue size-5 lg:size-6" />
          <div class="ml-3 lg:mt-0.5">
            {{ lawyer.position_in_office }}
          </div>
        </div>

        <div v-if="lawyer.lawyer_cities && lawyer.lawyer_cities.length > 0" class="mt-4 flex items-center">
          <IconLawyerAndFirmLocation class="text-lightBlue size-5 lg:size-6" />
          <div
            v-for="city in lawyer.lawyer_cities"
            :key="city"
            class="ml-3"
          >
            {{ city }}
          </div>
        </div>

        <div v-if="parsedLanguages" class="mt-4 flex items-center">
          <IconLawyerAndFirmGlobe class="text-lightBlue size-5 lg:size-6" />
          <div class="ml-3">
            {{ parsedLanguages }}
          </div>
        </div>

        <div v-if="lawyer.phone" class="mt-4 flex items-center h-6">
          <IconPhone class="text-lightBlue size-5 ml-1" />
          <a v-if="phoneVisible || showObscured" class="ml-3" :href="`tel:${lawyer.phone}`">
            {{ lawyer.phone }}
          </a>
          <button v-else class="ml-3 flex items-center" @click="showPhone">
            {{ obscureString(lawyer.phone) }}
            <ClientOnly>
              <el-tooltip  placement="top" :content="$t('click_to_show')" effect="blue">
                <IconEyeShow class="ml-2 size-7 text-gray-500 hover:text-lightBlue focus:outline-none"/>
              </el-tooltip>
            </ClientOnly>
          </button>        
        </div>

        <div v-if="lawyer.email" class="mt-4 flex items-center h-6">
          <IconEmail class="text-lightBlue size-5 ml-1" />
          <a v-if="emailVisible || showObscured" class="ml-3" :href="`mailto:${lawyer.email}`">
            {{ lawyer.email }}
          </a>
          <button v-else class="ml-3 flex items-center" @click="showEmail">
            {{ obscureEmail(lawyer.email) }}
            <ClientOnly>
              <el-tooltip  placement="top" :content="$t('click_to_show')" effect="blue">
                <IconEyeShow class="ml-2 size-7 text-gray-500 hover:text-lightBlue focus:outline-none"/>
              </el-tooltip>
            </ClientOnly> 
          </button>        
        </div>

        <GTModal :isOpen="loginModalVisible" :width="440" :closeIcon="true" @close="loginModalVisible = false">
          <template #header>
            <div class="flex justify-center mb-4">
              <IconLogin class="size-[50px]" />
            </div>
            {{ $t('need_to_login') }}
          </template>

          <template #content>
            <div class="flex justify-center my-6">
              <div class="text-center w-[330px]">
                {{ $t('need_to_login_description') }}
              </div>
            </div>
          </template>

          <template #footer>
            <div class="flex justify-center">
              <button class="w-[202px] h-12 text-white bg-darkBlue hover:bg-lightBlue rounded-full" @click="navigateToLogin">
                {{ t('login') }}
              </button>
            </div>
          </template>
        </GTModal>

        <div v-if="lawyer.bio" class="mt-6 leading-normal">
          {{ lawyer.bio }}
        </div>
        <div class="mt-4">
          <div class="flex">
            <!-- <NuxtLinkLocale to="proposal" class="mr-2 sm:mr-4 w-1/2 max-w-[282px]">
              <div class="h-11 font-semibold border border-darkBlue rounded-full flex justify-center items-center">
                <div class="ml-1">{{ $t('request_proposal') }}</div>
              </div>
            </NuxtLinkLocale> -->
            <NuxtLinkLocale :to="{ name: 'lawyerCalendar-firmLawyerId', params: { firmLawyerId: lawyer.firm_lawyer_id } }" class="w-1/2 max-w-[282px]" @click="sendGAEvent('lawyer_schedule_meeting_clicked')">
              <div class="bg-darkBlue hover:bg-lightBlue h-11 text-white font-semibold rounded-full flex justify-center items-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.95 18.35L7.4 14.8L8.85 13.35L10.95 15.45L15.15 11.25L16.6 12.7L10.95 18.35ZM5 22C4.45 22 3.97917 21.8042 3.5875 21.4125C3.19583 21.0208 3 20.55 3 20V6C3 5.45 3.19583 4.97917 3.5875 4.5875C3.97917 4.19583 4.45 4 5 4H6V2H8V4H16V2H18V4H19C19.55 4 20.0208 4.19583 20.4125 4.5875C20.8042 4.97917 21 5.45 21 6V20C21 20.55 20.8042 21.0208 20.4125 21.4125C20.0208 21.8042 19.55 22 19 22H5ZM5 20H19V10H5V20ZM5 8H19V6H5V8Z" fill="white"/>
                </svg>
                <div class="ml-1">{{ $t('schedule_meeting') }}</div>
              </div>
            </NuxtLinkLocale>
          </div>
        </div>
      </div>
    </div>
    
    <div v-if="lawyer.lawyer_specialisations && lawyer.lawyer_specialisations.length > 0" class="container mt-10 text-darkBlue">
      <div class="pl-3 lg:pl-12 lg:pr-8 py-8 rounded-[20px] shadow-[0_0_13px_2px_#DADADA80] text-sm lg:text-18">
        <div class="relative font-redhat font-semibold text-lg lg:text-36">
          {{ $t('lawyer_specialisation') }}
        </div>
        <div class="grid grid-cols-12 gap-4 items-center mb-4 mt-6 lg:mt-10 pr-4">
          <div class="hidden lg:block">
            {{ $t('number_abbreviation') }}
          </div>
          <div class="col-span-10 lg:col-span-9">
            {{ $t('specialisation_name') }}
          </div>
          <div class="col-span-2 flex relative right-[60px] lg:right-[100px]">
            <div class="relative left-[100%]">
              {{ $t('percentages') }}
            </div>
          </div>
        </div>
        <div v-for="(specialisation, index) in lawyer.lawyer_specialisations" :key="specialisation.id" class="mt-2 grid grid-cols-12 gap-4 items-center pr-4">
          <div class="hidden lg:block">
            0{{ index + 1 }}
          </div>
          <div class="col-span-6 lg:col-span-5">
            {{ specialisation.specialisation.name }}
          </div>
          <div class="col-span-4 ml-6">
            <el-progress :percentage="specialisation.percentage_of_experience" :show-text="false" />
          </div>
          <div class="col-span-2 flex justify-end">
            <div class="w-[60px] lg:w-[100px] h-[20px] lg:h-[40px] rounded-[10px] border-[1px] text-[12px] lg:text-18 border-lightBlue bg-white-300 text-lightBlue flex items-center justify-center">
              {{ specialisation.percentage_of_experience ?? 0 }}%
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="(lawyer.education && lawyer.education.length) || (lawyer.experience && lawyer.experience.length)" class="container mt-18 lg:mt-20 mb-10 text-white">
      <div class="bg-lightBlue p-10 pt-6 rounded-[40px] lg:flex">
        <div class="lg:w-1/2 lg:mr-[30px]">
          <div class="text-30 lg:text-36 font-redhat font-semibold">{{ $t('education')}}</div>
          <div v-for="education in lawyer.education" :key="education">
            <div class="mt-6 w-[65px] border-b-2 border-[#e8eaee]" />
            <div class="mt-6 text-18 lg:text-lg">
              {{ education.title }}
            </div>
            <div class="mt-2 font-redhat text-sm lg:text-md">
              {{ education.description }}
            </div>
          </div>
        </div>
        <div class="mt-12 lg:mt-0 lg:w-1/2 lg:ml-[30px]">
          <div class="text-30 lg:text-36">{{ $t('experience')}}</div>
          <div v-for="experience in lawyer.experience" :key="experience">
            <div class="mt-6 w-[65px] border-b-2 border-[#e8eaee]" />
            <div class="mt-6 text-18 lg:text-lg">
              {{ experience.title }}
            </div>
            <div class="mt-2 font-redhat text-sm lg:text-md">
              {{ experience.description }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="lawyer.photo" class="bg-darkBlue mt-18 lg:mt-20 lg:h-[766px] text-white" id="reviews">
      <div class="container">
        <div class="flex justify-center pt-20 text-36 lg:text-60 mb-10">
          {{ $t('lawyer_reviews') }}
        </div>
        <div class="flex justify-center mb-8">
          <button class="w-[195px] bg-white h-11 text-darkBlue font-semibold rounded-full flex justify-center items-center" @click="reviewModalVisible = true">
            {{ $t('leave_review') }}
          </button>
        </div>
        <div v-if="reviews.length" class="w-full bg-darkBlue">
          <Reviews :reviews="reviews" />
        </div>
        <div v-else class="flex justify-center mt-20">
          {{ $t('no_available_reviews') }}
        </div>
        <leave-review :is-visible="reviewModalVisible" :is-lawyer="true" :id="lawyer.id" @close="reviewModalVisible = false"/>
      </div>
    </div>
    
    <div class="flex justify-center">
      <div v-if="competingLawyersList && competingLawyersList.length > 0" class="mb-10 lg:mb-0 mt-10 container">
        <div class="flex justify-center text-darkBlue font-redhat font-semibold text-36 lg:text-60">
          {{ $t('top_rated_lawyers') }}
        </div>
        <div class="flex justify-center text-darkBlue font-redhat font-medium text-md mt-4 mb-6">
          {{ $t('top_rated_lawyers_description') }}
        </div>
        <div class="flex items-center justify-center">
          <NuxtLinkLocale
            :to="{
              name: 'lawyers-specialty',
              params: {
                specialty: $t('advocates').toLowerCase()
              }
            }"
            class="py-3 px-6 h-[48px] max-w-[202px] rounded-[9999px] mb-14 text-md text-white w-full bg-darkBlue inline-flex items-center justify-center"
          >
            {{ $t('search_lawyer') }}
          </NuxtLinkLocale>
        </div>
        <recommended-lawyers :recommended-lawyers-list="competingLawyersList"/>
      </div>
    </div>

  </div>
</template>
<style scoped>
.default-img{
  width: 423px;
  height: 423px;
  transform: translate(0.5%, 8%);
}

:deep(.el-progress-bar__outer) {
  height: 10px !important;
}
</style>
